/**
 * Drop-in replacement for react-router-dom's BrowserRouter that uses
 * synchronous state updates instead of React.startTransition().
 *
 * React Router v7's BrowserRouter unconditionally wraps navigation state
 * changes in startTransition. When components using useSyncExternalStore
 * (the editor, Zustand) are mounted, React's tearing detection can prevent
 * concurrent transitions from committing — the URL changes but the UI
 * stays on the old route.
 *
 * This component is a direct port of BrowserRouter's source with the
 * startTransition wrapper removed.
 *
 * @see docs/lessons-learned/react-router-tiptap-concurrent-nav-block.md
 */
import React, { useLayoutEffect, useState } from 'react';

import { Router, UNSAFE_createBrowserHistory as createBrowserHistory } from 'react-router-dom';

import type { BrowserRouterProps } from 'react-router-dom';

const SyncBrowserRouter: React.FC<BrowserRouterProps> = ({ basename, children, window }) => {
  const [history] = useState(() =>
    createBrowserHistory({ ...(window ? { window } : {}), v5Compat: true })
  );

  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(() => history.listen(setState), [history]);

  return (
    <Router
      {...(basename ? { basename } : {})}
      location={state.location}
      navigationType={state.action}
      navigator={history}
    >
      {children}
    </Router>
  );
};

export default SyncBrowserRouter;
